/**
 * Compatibility shim.
 *
 * Replaces the original localStorage-backed Zustand store with one that mirrors
 * the live database via the API. Existing pages keep their imports
 * (`useStore(s => s.projects)`, `useStore.getState().addX()`, …) — under the
 * hood every read/write now goes through the server.
 *
 * This shim is intentionally simple: after every mutation we refetch the
 * affected collection. For an internal tool with ≈ 7 users that's more than
 * fast enough. As individual pages are migrated to TanStack Query directly
 * we can shrink this file.
 */
import { create } from 'zustand';
import type {
  User,
  Client,
  Project,
  ProjectDocument,
  Stage,
  InvoiceMilestone,
  ChatMessage,
} from '../types';
import { apiClient, ApiError } from '../lib/apiClient';
import { isAdminRole } from '../lib/utils';
import { useAuthStore } from './authStore';

export interface AppError {
  id: string;
  message: string;
  operation: string;
  timestamp: number;
}

interface AppState {
  // Auth (delegated to authStore but exposed for backwards compat)
  currentUser: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;

  hydrated: boolean;
  hydrate: () => Promise<void>;

  users: User[];
  clients: Client[];
  projects: Project[];
  documents: ProjectDocument[];
  stages: Stage[];
  invoices: InvoiceMilestone[];
  messages: ChatMessage[];

  errors: AppError[];
  addError: (operation: string, err: unknown) => void;
  dismissError: (id: string) => void;
  clearErrors: () => void;

  refetchUsers: () => Promise<void>;
  refetchClients: () => Promise<void>;
  refetchProjects: () => Promise<void>;
  fetchMessages: (projectId: string) => Promise<void>;
  refetchAll: () => Promise<void>;

  addUser: (user: Omit<User, 'id'>) => Promise<void>;
  updateUser: (id: string, updates: Partial<User>) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  enableUser: (id: string) => Promise<void>;
  resetPassword: (id: string, newPassword?: string) => Promise<{ tempPassword: string }>;

  addClient: (client: Omit<Client, 'id' | 'createdAt'>) => Promise<void>;
  updateClient: (id: string, updates: Partial<Client>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;

  addProject: (project: Omit<Project, 'id' | 'createdAt' | 'overallCompletion'>) => Promise<void>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;

  addDocument: (doc: Omit<ProjectDocument, 'id'>) => Promise<void>;
  deleteDocument: (id: string) => Promise<void>;

  addStage: (stage: Omit<Stage, 'id'>) => Promise<void>;
  updateStage: (id: string, updates: Partial<Stage>) => Promise<void>;
  deleteStage: (id: string) => Promise<void>;
  reorderStages: (projectId: string, stageIds: string[]) => Promise<void>;

  addInvoice: (invoice: Omit<InvoiceMilestone, 'id'>) => Promise<void>;
  updateInvoice: (id: string, updates: Partial<InvoiceMilestone>) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;

  addMessage: (message: Omit<ChatMessage, 'id' | 'createdAt'>) => Promise<void>;
  resolveThread: (threadId: string) => Promise<void>;
}

interface ProjectListItem extends Project {
  client: { id: string; companyName: string };
}

interface ProjectDetail extends Project {
  client: Client;
  stages: Stage[];
  invoices: InvoiceMilestone[];
  documents: ProjectDocument[];
}

function projectToBase(p: ProjectListItem | ProjectDetail): Project {
  // Strip the eager-loaded relations so the shape matches the old `Project` type.
  const { client, ...rest } = p as ProjectListItem & Partial<{ stages: unknown; invoices: unknown; documents: unknown }>;
  void client;
  delete (rest as { stages?: unknown }).stages;
  delete (rest as { invoices?: unknown }).invoices;
  delete (rest as { documents?: unknown }).documents;
  return rest as Project;
}

export const useStore = create<AppState>()((set, get) => ({
  currentUser: null,

  hydrated: false,

  users: [],
  clients: [],
  projects: [],
  documents: [],
  stages: [],
  invoices: [],
  messages: [],

  errors: [],
  addError: (operation, err) => {
    // A 401 means the session expired — the global handler redirects to login,
    // so don't also show a confusing "Authentication required" toast.
    if (err instanceof ApiError && err.status === 401) return;
    const message = err instanceof Error ? err.message : String(err);
    const entry: AppError = { id: crypto.randomUUID(), message, operation, timestamp: Date.now() };
    set((s) => ({ errors: [entry, ...s.errors].slice(0, 20) }));
  },
  dismissError: (id) => set((s) => ({ errors: s.errors.filter((e) => e.id !== id) })),
  clearErrors: () => set({ errors: [] }),

  login: async (email, password) => {
    const r = await useAuthStore.getState().login(email, password);
    if (r.next === 'authenticated') {
      set({ currentUser: useAuthStore.getState().user });
      await get().hydrate();
      return true;
    }
    // Two-factor flow continues outside this shim — return false so old code
    // navigating to / on success won't bypass 2FA. Pages should use the real
    // Login component which handles the multi-step flow.
    return false;
  },

  logout: async () => {
    await useAuthStore.getState().logout();
    set({
      currentUser: null,
      hydrated: false,
      users: [],
      clients: [],
      projects: [],
      documents: [],
      stages: [],
      invoices: [],
      messages: [],
    });
  },

  hydrate: async () => {
    const auth = useAuthStore.getState();
    if (!auth.user || auth.twoFactorPending) return;
    set({ currentUser: auth.user });
    // Use allSettled so a single failed fetch doesn't strand the UI on the
    // workspace loading screen — surfaces any error in console without
    // blocking the rest of the app.
    const results = await Promise.allSettled([
      get().refetchUsers(),
      get().refetchClients(),
      get().refetchProjects(),
    ]);
    for (const r of results) {
      if (r.status === 'rejected') console.error('hydrate failed:', r.reason);
    }
    set({ hydrated: true });
  },

  refetchUsers: async () => {
    const auth = useAuthStore.getState();
    const endpoint = isAdminRole(auth.user?.role) ? '/users?includeDisabled=true' : '/users';
    const r = await apiClient.get<{ users: User[] }>(endpoint);
    set({ users: r.users });
  },

  refetchClients: async () => {
    const r = await apiClient.get<{ clients: Client[] }>('/clients');
    set({ clients: r.clients });
  },

  refetchProjects: async () => {
    const list = await apiClient.get<{ projects: ProjectListItem[] }>('/projects');
    const projects = list.projects.map(projectToBase);

    // Pull each project detail in parallel — eager-loads stages/invoices/documents/messages.
    const details = await Promise.all(
      list.projects.map((p) =>
        apiClient
          .get<{ project: ProjectDetail }>(`/projects/${p.id}`)
          .then((r) => r.project)
          .catch(() => null),
      ),
    );

    const stages: Stage[] = [];
    const invoices: InvoiceMilestone[] = [];
    const documents: ProjectDocument[] = [];
    for (const detail of details) {
      if (!detail) continue;
      stages.push(...detail.stages);
      invoices.push(...detail.invoices);
      documents.push(...detail.documents);
    }

    set({ projects, stages, invoices, documents });
  },

  fetchMessages: async (projectId) => {
    const r = await apiClient.get<{ messages: ChatMessage[] }>(`/messages?projectId=${encodeURIComponent(projectId)}`);
    set((s) => ({
      messages: [...s.messages.filter((m) => m.projectId !== projectId), ...r.messages],
    }));
  },

  refetchAll: async () => {
    await get().hydrate();
  },

  // ── Users ────────────────────────────────────────────────────────────────
  addUser: async (user) => {
    try {
      await apiClient.post('/users', user);
      await get().refetchUsers();
    } catch (err) { get().addError('addUser', err); throw err; }
  },
  updateUser: async (id, updates) => {
    try {
      await apiClient.patch(`/users/${id}`, updates);
      await get().refetchUsers();
    } catch (err) { get().addError('updateUser', err); throw err; }
  },
  deleteUser: async (id) => {
    try {
      await apiClient.delete(`/users/${id}`);
      await get().refetchUsers();
    } catch (err) { get().addError('deleteUser', err); throw err; }
  },
  enableUser: async (id) => {
    try {
      await apiClient.post(`/users/${id}/enable`, {});
      await get().refetchUsers();
    } catch (err) { get().addError('enableUser', err); throw err; }
  },
  resetPassword: async (id, newPassword) => {
    try {
      const r = await apiClient.post<{ tempPassword: string }>(`/users/${id}/reset-password`, newPassword ? { newPassword } : {});
      await get().refetchUsers();
      return r;
    } catch (err) { get().addError('resetPassword', err); throw err; }
  },

  // ── Clients ──────────────────────────────────────────────────────────────
  addClient: async (client) => {
    try {
      await apiClient.post('/clients', client);
      await get().refetchClients();
    } catch (err) { get().addError('addClient', err); throw err; }
  },
  updateClient: async (id, updates) => {
    try {
      await apiClient.patch(`/clients/${id}`, updates);
      await get().refetchClients();
    } catch (err) { get().addError('updateClient', err); throw err; }
  },
  deleteClient: async (id) => {
    try {
      await apiClient.delete(`/clients/${id}`);
      await get().refetchClients();
    } catch (err) { get().addError('deleteClient', err); throw err; }
  },

  // ── Projects ─────────────────────────────────────────────────────────────
  addProject: async (project) => {
    try {
      await apiClient.post('/projects', { ...project, autoGenerateTimeline: false });
      await get().refetchProjects();
    } catch (err) { get().addError('addProject', err); throw err; }
  },
  updateProject: async (id, updates) => {
    try {
      await apiClient.patch(`/projects/${id}`, updates);
      await get().refetchProjects();
    } catch (err) { get().addError('updateProject', err); throw err; }
  },
  deleteProject: async (id) => {
    try {
      await apiClient.delete(`/projects/${id}`);
      await get().refetchProjects();
    } catch (err) { get().addError('deleteProject', err); throw err; }
  },

  // ── Documents ────────────────────────────────────────────────────────────
  addDocument: async (_doc) => {
    await get().refetchProjects();
  },
  deleteDocument: async (id) => {
    try {
      await apiClient.delete(`/documents/${id}`);
      await get().refetchProjects();
    } catch (err) { get().addError('deleteDocument', err); throw err; }
  },

  // ── Stages ───────────────────────────────────────────────────────────────
  addStage: async (stage) => {
    try {
      await apiClient.post('/stages', stage);
      await get().refetchProjects();
    } catch (err) { get().addError('addStage', err); throw err; }
  },
  updateStage: async (id, updates) => {
    try {
      await apiClient.patch(`/stages/${id}`, updates);
      await get().refetchProjects();
    } catch (err) { get().addError('updateStage', err); throw err; }
  },
  deleteStage: async (id) => {
    try {
      await apiClient.delete(`/stages/${id}`);
      await get().refetchProjects();
    } catch (err) { get().addError('deleteStage', err); throw err; }
  },
  reorderStages: async (projectId, stageIds) => {
    try {
      await apiClient.post('/stages/reorder', { projectId, stageIds });
      await get().refetchProjects();
    } catch (err) { get().addError('reorderStages', err); throw err; }
  },

  // ── Invoices ─────────────────────────────────────────────────────────────
  addInvoice: async (invoice) => {
    try {
      await apiClient.post('/invoices', invoice);
      await get().refetchProjects();
    } catch (err) { get().addError('addInvoice', err); throw err; }
  },
  updateInvoice: async (id, updates) => {
    try {
      await apiClient.patch(`/invoices/${id}`, updates);
      await get().refetchProjects();
    } catch (err) { get().addError('updateInvoice', err); throw err; }
  },
  deleteInvoice: async (id) => {
    try {
      await apiClient.delete(`/invoices/${id}`);
      await get().refetchProjects();
    } catch (err) { get().addError('deleteInvoice', err); throw err; }
  },

  // ── Messages ─────────────────────────────────────────────────────────────
  addMessage: async (message) => {
    try {
      await apiClient.post('/messages', message);
      await get().fetchMessages(message.projectId);
    } catch (err) { get().addError('addMessage', err); throw err; }
  },
  resolveThread: async (threadId) => {
    try {
      const message = get().messages.find((m) => m.threadId === threadId);
      if (!message) return;
      await apiClient.post('/messages/resolve', { projectId: message.projectId, threadId });
      await get().fetchMessages(message.projectId);
    } catch (err) { get().addError('resolveThread', err); throw err; }
  },
}));

// Keep the shim's `currentUser` in lockstep with the auth store. Components
// that subscribe to `useStore(s => s.currentUser)` will re-render automatically.
useAuthStore.subscribe((state) => {
  useStore.setState({ currentUser: state.user });
});
